import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import ProductCard from '../ProductCard';

const ProductsPage = () => {
  useScrollAnimation();
  const [productsLoaded, setProductsLoaded] = useState(false);

  useEffect(() => {
    // MOBILE FIX: Set a timeout fallback to show products even if images don't load
    const timeout = setTimeout(() => {
      setProductsLoaded(true);
    }, 2000); // Show products after 2 seconds max

    // Preload critical images first
    const preloadCriticalImages = async () => {
      const criticalImages = [
        '/images/hero/product-hero.png',
        '/images/products/group.jpg'
      ];

      const imagePromises = criticalImages.map(src => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = src;
          img.onload = resolve;
          img.onerror = resolve; // Continue even if image fails
          // MOBILE FIX: Timeout individual images after 1.5 seconds
          setTimeout(resolve, 1500);
        });
      });

      await Promise.all(imagePromises);
      setProductsLoaded(true);
    };

    preloadCriticalImages();

    // MOBILE FIX: Cleanup timeout on unmount
    return () => clearTimeout(timeout);
  }, []);

  const yogurtFlavors = [
    {
      name: 'Banana',
      story: 'Smooth, sweet, and made with real Malawian banana puree — a creamy classic enjoyed by all ages.'
    },
    {
      name: 'Strawberry', 
      story: 'A rich and fruity blend made with real strawberry pieces for a refreshing authentic taste.'
    },
    {
      name: 'Pineapple',
      story: 'Bright and tropical, crafted using real pineapple for a naturally refreshing flavor.'
    },
    {
      name: 'Orange',
      story: 'A citrusy, zesty yogurt infused with real orange essence — smooth, tangy, and energizing.'
    },
    {
      name: 'Marula',
      story: 'A proudly Malawian favorite — exotic marula fruit blended into full-cream yogurt for a unique flavor experience.'
    }
  ];

  const baseSpecs = [
    { icon: 'fas fa-apple-alt', text: 'Real Fruit' },
    { icon: 'fas fa-cow', text: 'Full Cream Milk' },
    { icon: 'fas fa-heart', text: 'Source of Calcium' }
  ];

  const productSizes = [
    { size: '250ml', label: '250ml Cup' },
    { size: '500ml', label: '500ml Tub' }, 
    { size: '1L', label: '1 Litre Tub' }
  ];

  const yogurtProducts = yogurtFlavors.flatMap((flavor, fIndex) =>
    productSizes.map((s, sIndex) => {
      const flavorFile = flavor.name.toLowerCase();
      const sizeFile = s.size === '1L' ? '1ltr' : s.size.toLowerCase();
      return {
        id: fIndex * 3 + sIndex + 1,
        title: `${flavor.name} Yogurt`,
        size: s.size,
        description: s.size === '1L' 
          ? `Family-size ${flavor.name} yogurt — perfect for sharing or weekly use.`
          : `Delicious ${flavor.name.toLowerCase()} yogurt in a convenient ${s.size} pack.`,
        image: `/images/products/Flavored Yogurts/${flavorFile}${sizeFile}.png`,
        specs: [{ icon: 'fas fa-weight', text: s.label }, ...baseSpecs],
        badge: sIndex === 0 ? 'Popular' : sIndex === 1 ? 'Best Seller' : 'New'
      };
    })
  );

  return (
    <>
      <Helmet>
        <title>Riverton Dairy Products - Yogurt Flavors in Malawi</title>
        <meta
          name="description"
          content="Explore Riverton Dairy's full range of creamy yogurt flavors in Malawi. Made with real fruit and full cream milk, MBS certified and loved nationwide."
        />
        <meta
          name="keywords"
          content="Riverton Dairy, Yogurt Malawi, Flavored Yogurt, Real Fruit Yogurt, Full Cream Milk, Banana Yogurt, Strawberry Yogurt, Pineapple Yogurt, Orange Yogurt, Marula Yogurt"
        />
        <meta name="author" content="Riverton Dairy" />
        
        {/* Preload critical images */}
        <link rel="preload" href="/images/hero/product-hero.png" as="image" />
        <link rel="preload" href="/images/products/group.jpg" as="image" />

        <meta property="og:title" content="Riverton Dairy Products - Yogurt Flavors in Malawi" />
        <meta
          property="og:description"
          content="Discover the delicious yogurt range by Riverton Dairy in Malawi. Creamy, natural, and MBS certified."
        />
        <meta property="og:image" content="/images/hero/product-hero.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      <section
        className="products-hero scroll-animate"
        style={{ backgroundImage: `url('/images/hero/product-hero.png')` }}
      >
        <div className="container">
          <h1>Our Yogurt Range</h1>
          <p>Creamy, delicious, and made with real fruit — Riverton Dairy brings joy to every spoonful</p>
        </div>
      </section>

      <section className="product-categories scroll-animate">
        <div className="container">
          <div className="section-header">
            <h2>Proudly Malawian Flavors</h2>
            <p>Discover the unique taste profiles of our yogurt flavors — each crafted with real fruit and local milk.</p>
          </div>

          <div className="categories-grid">
            {yogurtFlavors.map((flavor, idx) => (
              <div key={idx} className="category-card" style={{ animationDelay: `${idx * 0.15}s` }}>
                <div
                  className="category-image"
                  style={{ backgroundImage: `url('/images/products/${flavor.name.toLowerCase()}-range.png')` }}
                ></div>
                <div className="category-content">
                  <h3>{flavor.name} Flavor</h3>
                  <p>{flavor.story}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="products-section scroll-animate">
        <div className="container">
          <div className="section-header">
            <h2>Available in Three Sizes</h2>
            <p>Choose the perfect size for you — from single-serve cups to family tubs.</p>
          </div>

          {!productsLoaded ? (
            <div className="products-loading">
              <div className="loading-spinner-large"></div>
              <p>Loading delicious products...</p>
            </div>
          ) : (
            <div className="products-grid">
              {yogurtProducts.map(product => (
                <ProductCard 
                  key={`${product.title}-${product.size}`} 
                  product={product} 
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="stats-section scroll-animate">
        <div className="container">
          <div className="stats-container">
            <div className="stats-content">
              <h2>Why Malawians Love Riverton Yogurt</h2>
              <p>Made with pride in Lilongwe, supporting local farmers and delivering real taste in every cup.</p>

              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-number">5</span>
                  <span className="stat-label">Delicious Flavors</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">100%</span>
                  <span className="stat-label">Real Fruit & Milk</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">MBS</span>
                  <span className="stat-label">Certified Quality</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">Proudly Malawian</span>
                </div>
              </div>
            </div>

            <div className="stats-image">
              <div className="image-wrapper">
                <img
                  src="/images/products/group.jpg"
                  alt="Riverton Dairy team and Malawian farmers"
                  className="group-photo"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductsPage;